import { Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

export const ClientCombobox = ({
  clients,
  onNameChange,
  onSelect,
  selectedClientId,
  value,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const selectedClient = clients.find((client) => client.client_id === selectedClientId);
  const normalizedQuery = query.trim().toLocaleLowerCase("fr-FR");
  const matches = useMemo(() => {
    if (!normalizedQuery) {
      return [];
    }

    return clients.filter((client) => {
      const searchable = [client.name, client.phone, client.address]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("fr-FR");
      return searchable.includes(normalizedQuery);
    }).slice(0, 12);
  }, [clients, normalizedQuery]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const selectClient = (client) => {
    onSelect(client);
    setQuery("");
    setIsOpen(false);
  };

  const clearSelection = () => {
    onSelect(null);
    onNameChange("");
    setQuery("");
    setIsOpen(true);
  };

  return (
    <div
      className="product-combobox sale-client-combobox"
      data-testid="sale-client-combobox"
      ref={containerRef}
    >
      <Search aria-hidden="true" size={16} />
      <input
        aria-controls="sale-client-options"
        aria-expanded={isOpen}
        aria-label="Rechercher un client"
        autoComplete="off"
        data-testid="sale-client-combobox-input"
        onChange={(event) => {
          const nextName = event.target.value;
          onNameChange(nextName);
          setQuery(nextName);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setIsOpen(false);
          }

          if (event.key === "Enter" && isOpen && matches.length) {
            event.preventDefault();
            selectClient(matches[0]);
          }
        }}
        placeholder="Rechercher un client existant"
        role="combobox"
        value={isOpen ? query : selectedClient?.name || value}
      />
      {selectedClient && (
        <button
          aria-label="Choisir ou créer un autre client"
          data-testid="sale-client-combobox-clear-button"
          onClick={clearSelection}
          title="Choisir ou créer un autre client"
          type="button"
        >
          <X size={15} />
        </button>
      )}
      {isOpen && (
        <div
          className="product-combobox-menu"
          data-testid="sale-client-combobox-menu"
          id="sale-client-options"
          role="listbox"
        >
          {!normalizedQuery && (
            <p data-testid="sale-client-combobox-prompt">
              Saisissez un nom, téléphone ou une adresse.
            </p>
          )}
          {normalizedQuery && matches.map((client) => (
            <button
              aria-selected={client.client_id === selectedClientId}
              className="product-combobox-option"
              data-testid={`sale-client-option-${client.client_id}`}
              key={client.client_id}
              onMouseDown={(event) => {
                event.preventDefault();
                selectClient(client);
              }}
              role="option"
              type="button"
            >
              <strong>{client.name}</strong>
              <span>{client.phone} · {client.address || "Adresse à compléter"}</span>
            </button>
          ))}
          {normalizedQuery && !matches.length && (
            <p data-testid="sale-client-combobox-empty">Aucun client correspondant.</p>
          )}
        </div>
      )}
    </div>
  );
};